import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';
import UploadProjectModal from '../../components/UploadProjectModal/UploadProjectModal';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import ProjectDetailPage from './ProjectDetailPage';
import './MarketplacePage.css';

function MarketplacePage() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  
  // If there's a project ID in the URL, show the detail page
  if (projectId) {
    return <ProjectDetailPage />;
  }
  
  return <MarketplaceList />;
}

function MarketplaceList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(100);
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsRef = collection(db, 'marketplace-projects');
      const q = query(projectsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const projectsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Fetched projects:', projectsList);
      projectsList.forEach(p => {
        console.log(`Project "${p.name}":`, {
          imageUrl: p.imageUrl,
          imageFileName: p.imageFileName,
          folderName: p.folderName
        });
      });
      
      setProjects(projectsList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectUploaded = () => {
    setUploadModalOpen(false);
    fetchProjects();
  };

  const priceMetrics = useMemo(() => {
    const prices = projects.map((project) => Number(project.price) || 0);
    const rawMax = Math.max(0, ...prices);
    const sliderMax = Math.max(25, Math.ceil(rawMax / 25) * 25);

    return {
      sliderMax,
    };
  }, [projects]);

  useEffect(() => {
    setPriceMax((prev) => {
      if (!Number.isFinite(prev) || prev <= 0 || prev > priceMetrics.sliderMax) {
        return priceMetrics.sliderMax;
      }
      return prev;
    });
  }, [priceMetrics.sliderMax]);

  useEffect(() => {
    setPriceMin((prev) => {
      if (!Number.isFinite(prev) || prev < 0) {
        return 0;
      }
      return Math.min(prev, priceMax);
    });
  }, [priceMax]);

  const filteredAndSortedProjects = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();

    const filtered = projects.filter((project) => {
      const name = (project.name || '').toLowerCase();
      const owner = (project.ownerName || '').toLowerCase();
      const description = (project.description || '').toLowerCase();
      const combinedText = `${name} ${owner} ${description}`;
      const matchesSearch = !search || combinedText.includes(search);

      const numericPrice = Number(project.price) || 0;
      const matchesPriceRange = numericPrice >= priceMin && numericPrice <= priceMax;

      const isMine = Boolean(currentUser && currentUser.uid === project.ownerId);
      const matchesOwnership =
        ownershipFilter === 'all'
          ? true
          : ownershipFilter === 'mine'
          ? isMine
          : !isMine;

      const hasImage = Boolean(
        (project.imageUrls && project.imageUrls.length) || project.imageUrl
      );
      const matchesMedia =
        mediaFilter === 'all'
          ? true
          : mediaFilter === 'with-image'
          ? hasImage
          : !hasImage;

      return matchesSearch && matchesPriceRange && matchesOwnership && matchesMedia;
    });

    const sorted = [...filtered].sort((a, b) => {
      const aCreated = a.createdAt?.toMillis?.() || 0;
      const bCreated = b.createdAt?.toMillis?.() || 0;
      const aPrice = Number(a.price) || 0;
      const bPrice = Number(b.price) || 0;
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();

      switch (sortBy) {
        case 'oldest':
          return aCreated - bCreated;
        case 'price-low':
          return aPrice - bPrice;
        case 'price-high':
          return bPrice - aPrice;
        case 'name-az':
          return aName.localeCompare(bName);
        case 'name-za':
          return bName.localeCompare(aName);
        case 'newest':
        default:
          return bCreated - aCreated;
      }
    });

    return sorted;
  }, [projects, searchQuery, sortBy, priceMin, priceMax, ownershipFilter, mediaFilter, currentUser]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setSortBy('newest');
    setPriceMin(0);
    setPriceMax(priceMetrics.sliderMax);
    setOwnershipFilter('all');
    setMediaFilter('all');
  };

  const activeFilterCount = [
    sortBy !== 'newest',
    priceMin > 0,
    priceMax < priceMetrics.sliderMax,
    ownershipFilter !== 'all',
    mediaFilter !== 'all',
  ].filter(Boolean).length;

  const priceMinPercent = priceMetrics.sliderMax > 0
    ? (priceMin / priceMetrics.sliderMax) * 100
    : 0;
  const priceMaxPercent = priceMetrics.sliderMax > 0
    ? (priceMax / priceMetrics.sliderMax) * 100
    : 100;

  return (
    <div className="marketplace-shell">
      <header className="marketplace-header">
        <div className="marketplace-header__content">
          <h1>Project Marketplace</h1>
          <p>Browse and share robot behavior tree projects</p>
        </div>
        {currentUser && (
          <button
            className="marketplace-upload-btn"
            onClick={() => setUploadModalOpen(true)}
          >
            + Upload Project
          </button>
        )}
      </header>

      <div className="marketplace-layout">
        <aside className="marketplace-sidebar" aria-label="Marketplace search and filters">
          <section className="marketplace-controls">
            <div className="marketplace-controls__search-wrap">
              <input
                type="search"
                className="marketplace-controls__search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by project name, creator, or keyword"
                aria-label="Search projects"
              />
            </div>

            <div className="marketplace-controls__toggle-row">
              <button
                type="button"
                className="marketplace-controls__toggle"
                onClick={() => setFiltersOpen((prev) => !prev)}
                aria-expanded={filtersOpen}
                aria-controls="marketplace-filter-panel"
              >
                <span className="marketplace-controls__icon marketplace-controls__icon--filter" aria-hidden="true" />
                <span className="marketplace-controls__icon marketplace-controls__icon--sort" aria-hidden="true" />
                {filtersOpen ? 'Hide filters and sort' : 'Show filters and sort'}
              </button>
              {activeFilterCount > 0 && (
                <span className="marketplace-controls__active-chip">
                  {activeFilterCount} active
                </span>
              )}
            </div>

            {filtersOpen && (
              <div id="marketplace-filter-panel" className="marketplace-controls__filters">
                <label>
                  <span className="marketplace-controls__label-row">
                    <span className="marketplace-controls__icon marketplace-controls__icon--sort" aria-hidden="true" />
                    Sort
                  </span>
                  <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                    <option value="newest">Newest listed</option>
                    <option value="oldest">Oldest listed</option>
                    <option value="price-low">Price: low to high</option>
                    <option value="price-high">Price: high to low</option>
                    <option value="name-az">Name: A-Z</option>
                    <option value="name-za">Name: Z-A</option>
                  </select>
                </label>

                <section className="marketplace-controls__price-panel" aria-label="Price filters">
                  <div className="marketplace-controls__label-row">
                    <span className="marketplace-controls__icon marketplace-controls__icon--tag" aria-hidden="true" />
                    Price
                  </div>

                  <div className="marketplace-controls__price-slider-wrap">
                    <div className="marketplace-controls__price-slider-header">
                      <span>Min: ${priceMin.toFixed(0)}</span>
                      <strong>Max: ${priceMax.toFixed(0)}</strong>
                    </div>
                    <div className="marketplace-controls__double-slider">
                      <div className="marketplace-controls__double-slider-track" aria-hidden="true">
                        <span className="marketplace-controls__double-slider-track-base" />
                        <span
                          className="marketplace-controls__double-slider-track-active"
                          style={{
                            left: `${priceMinPercent}%`,
                            width: `${Math.max(0, priceMaxPercent - priceMinPercent)}%`,
                          }}
                        />
                      </div>

                      <input
                        type="range"
                        min="0"
                        max={priceMetrics.sliderMax}
                        step="1"
                        value={priceMin}
                        onChange={(event) => {
                          const nextMin = Number(event.target.value);
                          setPriceMin(Math.min(nextMin, priceMax));
                        }}
                        className="marketplace-controls__range marketplace-controls__range--min"
                        aria-label="Minimum price"
                      />
                      <input
                        type="range"
                        min="0"
                        max={priceMetrics.sliderMax}
                        step="1"
                        value={priceMax}
                        onChange={(event) => {
                          const nextMax = Number(event.target.value);
                          setPriceMax(Math.max(nextMax, priceMin));
                        }}
                        className="marketplace-controls__range marketplace-controls__range--max"
                        aria-label="Maximum price"
                      />
                    </div>
                  </div>
                </section>

                <label>
                  <span className="marketplace-controls__label-row">
                    <span className="marketplace-controls__icon marketplace-controls__icon--owner" aria-hidden="true" />
                    Listing owner
                  </span>
                  <select value={ownershipFilter} onChange={(event) => setOwnershipFilter(event.target.value)}>
                    <option value="all">Everyone</option>
                    <option value="mine">My listings</option>
                    <option value="others">Other creators</option>
                  </select>
                </label>

                <label>
                  <span className="marketplace-controls__label-row">
                    <span className="marketplace-controls__icon marketplace-controls__icon--image" aria-hidden="true" />
                    Preview image
                  </span>
                  <select value={mediaFilter} onChange={(event) => setMediaFilter(event.target.value)}>
                    <option value="all">All listings</option>
                    <option value="with-image">With image</option>
                    <option value="no-image">Without image</option>
                  </select>
                </label>
              </div>
            )}

            <div className="marketplace-controls__footer">
              <span>{filteredAndSortedProjects.length} result{filteredAndSortedProjects.length === 1 ? '' : 's'}</span>
              <button type="button" onClick={handleResetFilters}>
                Reset filters
              </button>
            </div>
          </section>
        </aside>

        <section className="marketplace-results" aria-live="polite">
          {loading ? (
            <div className="marketplace-loading">Loading projects...</div>
          ) : filteredAndSortedProjects.length === 0 ? (
            <div className="marketplace-empty">
              <h2>No matching projects</h2>
              <p>Try changing your search or filters.</p>
            </div>
          ) : (
            <div className="marketplace-grid">
              {filteredAndSortedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  currentUser={currentUser}
                  onProjectDeleted={fetchProjects}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {uploadModalOpen && (
        <UploadProjectModal
          onClose={() => setUploadModalOpen(false)}
          onSuccess={handleProjectUploaded}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}

export default MarketplacePage;
